import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { getHubSpotDealsBoard, getHubSpotDealCountAllStages, getHubSpotDealsByStageAndOwner, getHubSpotQuarterlyComparison, getHubSpotFunnelAge, uploadRevenue, getQuarterlyRevenue, getHubSpotDealsFiltered } from '../services/api';
import { useLayout } from '../context/LayoutContext';
import useSortableData from '../hooks/useSortableData';

const STAGE_COLORS = [
  '#0891b2', '#0d9488', '#059669', '#2563eb', '#7c3aed', '#c026d3', '#e11d48'
];

function formatAmount(val) {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val.toLocaleString();
}

function DealCard({ deal }) {
  const [showScorePopup, setShowScorePopup] = React.useState(false);
  const [popupPos, setPopupPos] = React.useState({ top: 0, left: 0 });
  const scoreRef = React.useRef(null);

  const openPopup = (e) => {
    e.stopPropagation();
    if (showScorePopup) { setShowScorePopup(false); return; }
    const rect = scoreRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceRight = window.innerWidth - rect.right;
      const spaceBelow = window.innerHeight - rect.bottom;
      setPopupPos({
        top: spaceBelow > 280 ? rect.bottom + 6 : rect.top - 280,
        left: spaceRight > 260 ? rect.left : rect.right - 250
      });
    }
    setShowScorePopup(true);
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '14px',
      marginBottom: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.15s',
      cursor: 'default'
    }}
    onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'}
    onMouseOut={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}
    >
      {/* Deal Name */}
      <div style={{
        fontSize: '13px',
        fontWeight: '600',
        color: '#0e7490',
        marginBottom: '10px',
        lineHeight: '1.3',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical'
      }} title={deal.deal_name}>
        {deal.deal_name}
      </div>

      {/* Details */}
      <div style={{ fontSize: '12px', color: '#334155', lineHeight: '1.7' }}>
        <div><strong>Amount:</strong> {deal.currency} {deal.amount > 0 ? deal.amount.toLocaleString() : '-'}</div>
        <div><strong>Close date:</strong> {deal.close_date || '-'}</div>
        <div><strong>Deal owner:</strong> {deal.deal_owner || '-'}</div>
        <div><strong>Create date:</strong> {deal.create_date || '-'}</div>
      </div>

      {/* Deal Score */}
      {deal.deal_score > 0 && (() => {
        const score = deal.deal_score;
        const color = score >= 60 ? '#16a34a' : score >= 30 ? '#d97706' : '#ef4444';
        const bg = score >= 60 ? '#f0fdf4' : score >= 30 ? '#fffbeb' : '#fef2f2';
        const probPct = deal.probability_pct;
        const goodItem = (text) => <div style={{ fontSize: '11px', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ fontSize: '9px' }}>{'\u25B2'}</span> {text}</div>;
        const badItem = (text) => <div style={{ fontSize: '11px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ fontSize: '9px' }}>{'\u25BC'}</span> {text}</div>;
        return (
          <div style={{ marginTop: '10px', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span ref={scoreRef} onClick={openPopup} style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '30px', height: '30px', borderRadius: '50%',
                border: `2.5px solid ${color}`, background: bg,
                fontSize: '11px', fontWeight: '800', color, cursor: 'pointer'
              }}>
                {score}
              </span>
            </div>
            {showScorePopup && (
              <>
                <div onClick={() => setShowScorePopup(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} />
                <div style={{
                  position: 'fixed', top: popupPos.top, left: popupPos.left, zIndex: 999,
                  background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.18)', padding: '14px',
                  width: '250px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: '36px', height: '36px', borderRadius: '50%',
                      border: `3px solid ${color}`, background: bg,
                      fontSize: '14px', fontWeight: '800', color, flexShrink: 0
                    }}>{score}</span>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Key factors</div>
                  </div>

                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>Progression</div>
                  {probPct <= 20 ? badItem(`Deal probability is ${probPct}%`) : goodItem(`Deal probability is ${probPct}%`)}
                  {deal.days_in_funnel > 180 ? badItem(`${deal.days_in_funnel} days in funnel`) : goodItem(`${deal.days_in_funnel} days in funnel`)}

                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', marginTop: '10px', marginBottom: '4px' }}>Engagement</div>
                  {deal.sales_activities > 0 ? goodItem(`${deal.sales_activities} sales activities`) : badItem('No sales activities')}
                  {deal.contacts > 0 ? goodItem(`${deal.contacts} associated contacts`) : badItem('No associated contacts')}

                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', marginTop: '10px', marginBottom: '4px' }}>Recency of engagement</div>
                  {deal.days_since_last_activity <= 30 ? goodItem(`${deal.days_since_last_activity} days since last activity`) : badItem(`${deal.days_since_last_activity} days since last activity`)}
                </div>
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function StageColumn({ column, colorIndex }) {
  const color = STAGE_COLORS[colorIndex % STAGE_COLORS.length];
  const probPct = column.probability_pct;

  return (
    <div style={{
      minWidth: '280px',
      maxWidth: '320px',
      flex: '0 0 280px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Column Header */}
      <div style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderBottom: `3px solid ${color}`,
        borderRadius: '8px 8px 0 0',
        padding: '12px 14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#1e293b'
          }}>
            {column.stage_label}
          </span>
          <span style={{
            background: color,
            color: '#fff',
            padding: '1px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: '700'
          }}>
            {column.deal_count}
          </span>
        </div>
        <span style={{ color: '#94a3b8', fontSize: '16px', cursor: 'pointer' }}>&lsaquo;</span>
      </div>

      {/* Cards Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '10px',
        background: '#f1f5f9',
        borderLeft: '1px solid #e2e8f0',
        borderRight: '1px solid #e2e8f0'
      }}>
        {column.deals.map((deal, idx) => (
          <DealCard key={deal.id || idx} deal={deal} />
        ))}
        {column.deals.length === 0 && (
          <div style={{ textAlign: 'center', padding: '30px 10px', color: '#94a3b8', fontSize: '13px' }}>
            No deals
          </div>
        )}
      </div>

      {/* Column Footer */}
      <div style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderTop: '2px solid #e2e8f0',
        borderRadius: '0 0 8px 8px',
        padding: '10px 14px',
        fontSize: '12px',
        color: '#334155',
        lineHeight: '1.8',
        minHeight: '58px'
      }}>
        <div>
          <span style={{ fontWeight: '800' }}>USD {formatAmount(column.total_amount)}</span>
          <span style={{ color: '#94a3b8' }}> | Total amount</span>
        </div>
        <div>
          <span style={{ fontWeight: '800' }}>USD {formatAmount(column.total_weighted)}</span>
          <span style={{ color: '#94a3b8' }}> ({probPct}%) | Weighted amount</span>
        </div>
      </div>
    </div>
  );
}

function mergeBoards(boards) {
  // Merge multiple year results into one board
  const columnMap = {};
  for (const board of boards) {
    for (const col of (board.columns || [])) {
      if (!columnMap[col.stage_id]) {
        columnMap[col.stage_id] = { ...col, deals: [...col.deals] };
      } else {
        columnMap[col.stage_id].deals.push(...col.deals);
        columnMap[col.stage_id].total_amount += col.total_amount;
        columnMap[col.stage_id].total_weighted += col.total_weighted;
        columnMap[col.stage_id].deal_count += col.deal_count;
      }
    }
  }
  const sorted = Object.values(columnMap).sort((a, b) => a.order - b.order);
  for (const col of sorted) {
    col.deals.sort((a, b) => b.amount - a.amount);
    col.total_amount = Math.round(col.total_amount * 100) / 100;
    col.total_weighted = Math.round(col.total_weighted * 100) / 100;
  }
  return sorted;
}

function DealsBoardDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [boardData, setBoardData] = useState(null);
  const [stageCountData, setStageCountData] = useState(null);
  const [stageOwnerData, setStageOwnerData] = useState(null);
  const [stageOwnerOwners, setStageOwnerOwners] = useState([]);
  const [quarterlyData, setQuarterlyData] = useState(null);
  const [selectedDealView, setSelectedDealView] = useState(null);
  const [funnelAgeData, setFunnelAgeData] = useState(null);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [chartPeriod, setChartPeriod] = useState('year'); // 'year', 'this_q', 'last_q'
  const [chartDrillDeals, setChartDrillDeals] = useState(null); // {label, deals} for bottom chart click

  // Sortable hooks for all tables
  const { sortedData: sortedQtrDeals, requestSort: reqQtrSort, getSortIndicator: getQtrSortInd } = useSortableData(selectedDealView?.deals || []);
  const { sortedData: sortedRevClients, requestSort: reqRevSort, getSortIndicator: getRevSortInd } = useSortableData(selectedDealView?.revenueClients || []);
  const { sortedData: sortedFunnelDeals, requestSort: reqFunnelSort, getSortIndicator: getFunnelSortInd } = useSortableData(selectedBucket?.deals || []);
  const { sortedData: sortedDrillDeals, requestSort: reqDrillSort, getSortIndicator: getDrillSortInd } = useSortableData(chartDrillDeals?.deals || []);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  // Board filters
  const [filterOwner, setFilterOwner] = useState('');
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [revenueData, setRevenueData] = useState(null);
  const [selectedYears, setSelectedYears] = useState([new Date().getFullYear()]);
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'report'
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const { mobileLayout, toggleLayout } = useLayout();

  const availableYears = [2024, 2025, 2026, 2027];

  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);

  const toggleYear = (year) => {
    setSelectedYears(prev => {
      if (prev.includes(year)) {
        if (prev.length === 1) return prev; // keep at least one
        return prev.filter(y => y !== year);
      }
      return [...prev, year].sort();
    });
  };

  const toggleAllYears = () => {
    if (selectedYears.length === availableYears.length) {
      setSelectedYears([new Date().getFullYear()]);
    } else {
      setSelectedYears([...availableYears]);
    }
  };

  // Determine quarter numbers
  const currentQ = Math.ceil((new Date().getMonth() + 1) / 3);
  const lastQ = currentQ === 1 ? 4 : currentQ - 1;

  const getChartQuarter = (period) => {
    if (period === 'this_q') return currentQ;
    if (period === 'last_q') return lastQ;
    return null; // full year
  };

  const reportYear = new Date().getFullYear();

  const chartPeriodLabel = chartPeriod === 'this_q' ? `Q${currentQ} ${reportYear}`
    : chartPeriod === 'last_q' ? `Q${lastQ} ${currentQ === 1 ? reportYear - 1 : reportYear}`
    : `${reportYear}`;

  // Helper: fetch one at a time with delay to avoid HubSpot rate limits
  const fetchSequential = async (fetchFn, items, delayMs = 500) => {
    const results = [];
    for (const item of items) {
      results.push(await fetchFn(item));
      if (items.length > 1) await new Promise(r => setTimeout(r, delayMs));
    }
    return results;
  };

  const fetchChartData = async (period) => {
    const quarter = getChartQuarter(period);

    const stageRes = await getHubSpotDealCountAllStages(reportYear, quarter);
    setStageCountData((stageRes.data || []).sort((a, b) => a.order - b.order));

    await new Promise(r => setTimeout(r, 500));

    const ownerRes = await getHubSpotDealsByStageAndOwner(reportYear, quarter);
    const allOwners = new Set();
    const soMerged = {};
    (ownerRes.owners || []).forEach(o => allOwners.add(o));
    for (const row of (ownerRes.data || [])) {
      if (!soMerged[row.stage]) {
        soMerged[row.stage] = { stage: row.stage, order: row.order, total: 0 };
      }
      soMerged[row.stage].total += row.total;
      for (const owner of (ownerRes.owners || [])) {
        soMerged[row.stage][owner] = (soMerged[row.stage][owner] || 0) + (row[owner] || 0);
      }
    }
    setStageOwnerData(Object.values(soMerged).sort((a, b) => a.order - b.order));
    setStageOwnerOwners(Array.from(allOwners).sort());
  };

  useEffect(() => {
    fetchData();
  }, [selectedYears]);

  // Re-fetch chart data when period pill changes (without full reload)
  useEffect(() => {
    if (!loading && boardData) {
      setSelectedStage(null);
      setSelectedOwner(null);
      setChartDrillDeals(null);
      fetchChartData(chartPeriod).catch(e => console.error('Chart period fetch failed:', e));
    }
  }, [chartPeriod]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch board data sequentially
      const boardResults = await fetchSequential(getHubSpotDealsBoard, selectedYears);
      const merged = mergeBoards(boardResults);
      const totalDeals = merged.reduce((sum, c) => sum + c.deal_count, 0);
      // Merge metrics: use last year's metrics as base, or sum across years
      const lastBoard = boardResults[boardResults.length - 1];
      const mergedMetrics = lastBoard?.metrics || {};
      const mergedOwners = [...new Set(boardResults.flatMap(b => b.owners || []))].sort();
      setBoardData({ columns: merged, total_deals: totalDeals, years: selectedYears, metrics: mergedMetrics, owners: mergedOwners });

      // Fetch report data sequentially to avoid rate limits
      try {
        await fetchChartData(chartPeriod);

        const qtrData = await getHubSpotQuarterlyComparison(reportYear);
        await new Promise(r => setTimeout(r, 500));
        setQuarterlyData(qtrData);

        const funnelData = await getHubSpotFunnelAge(reportYear);
        setFunnelAgeData(funnelData);

        const revData = await getQuarterlyRevenue();
        setRevenueData(revData);
      } catch (e) {
        console.error('Report data fetch failed (rate limit?):', e);
      }
    } catch (err) {
      setError('Failed to fetch deals board data. Make sure the backend is running.');
      console.error('Error fetching deals board:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const columns = boardData?.columns || [];

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header" style={{
        display: 'flex',
        flexDirection: mobileLayout ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: mobileLayout ? 'flex-start' : 'center',
        marginBottom: '20px',
        gap: mobileLayout ? '12px' : '0'
      }}>
        <div>
          <h1 style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: mobileLayout ? '16px' : undefined
          }}>
            <span style={{ color: '#f97316' }}>&#9734;</span>
            Deals Board
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
            Pipeline view grouped by deal stage
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Layout Toggle */}
          <button
            onClick={toggleLayout}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: mobileLayout ? '#3b82f6' : '#fff',
              color: mobileLayout ? '#fff' : '#1e293b',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {mobileLayout ? 'Mobile' : 'Desktop'}
          </button>
          {/* Year Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setYearDropdownOpen(prev => !prev)}
              style={{
                padding: '8px 14px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: '140px',
                justifyContent: 'space-between'
              }}
            >
              <span>
                {selectedYears.length === availableYears.length
                  ? 'All Years'
                  : selectedYears.join(', ')}
              </span>
              <span style={{ fontSize: '10px', color: '#64748b' }}>{yearDropdownOpen ? '\u25B2' : '\u25BC'}</span>
            </button>
            {yearDropdownOpen && (
              <>
                <div
                  onClick={() => setYearDropdownOpen(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }}
                />
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  zIndex: 999,
                  minWidth: '160px',
                  padding: '6px 0'
                }}>
                  {/* All Years option */}
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: selectedYears.length === availableYears.length ? '700' : '400',
                    color: selectedYears.length === availableYears.length ? '#1e40af' : '#334155',
                    background: selectedYears.length === availableYears.length ? '#eff6ff' : 'transparent',
                    borderBottom: '1px solid #f1f5f9'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedYears.length === availableYears.length}
                      onChange={toggleAllYears}
                      style={{ accentColor: '#2563eb', cursor: 'pointer' }}
                    />
                    All Years
                  </label>
                  {availableYears.map(year => (
                    <label key={year} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: selectedYears.includes(year) ? '700' : '400',
                      color: selectedYears.includes(year) ? '#1e40af' : '#334155',
                      background: selectedYears.includes(year) ? '#eff6ff' : 'transparent'
                    }}
                    onMouseOver={e => { if (!selectedYears.includes(year)) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseOut={e => { if (!selectedYears.includes(year)) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedYears.includes(year)}
                        onChange={() => toggleYear(year)}
                        style={{ accentColor: '#2563eb', cursor: 'pointer' }}
                      />
                      {year}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          {/* View Toggle */}
          <div style={{
            display: 'flex',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <button
              onClick={() => setViewMode('board')}
              style={{
                padding: '8px 14px',
                border: 'none',
                background: viewMode === 'board' ? '#1e40af' : '#fff',
                color: viewMode === 'board' ? '#fff' : '#64748b',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'all 0.15s'
              }}
            >
              Board View
            </button>
            <button
              onClick={() => setViewMode('report')}
              style={{
                padding: '8px 14px',
                border: 'none',
                borderLeft: '1px solid #e2e8f0',
                background: viewMode === 'report' ? '#1e40af' : '#fff',
                color: viewMode === 'report' ? '#fff' : '#64748b',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'all 0.15s'
              }}
            >
              Report View
            </button>
          </div>
          {/* Upload Revenue Button */}
          {viewMode === 'report' && (
            <>
              <label style={{
                padding: '8px 14px',
                borderRadius: '6px',
                border: '1px solid #10b981',
                background: uploading ? '#f0fdf4' : '#fff',
                color: '#059669',
                fontSize: '13px',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s'
              }}>
                {uploading ? 'Uploading...' : 'Upload Revenue'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  disabled={uploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploading(true);
                    setUploadResult(null);
                    try {
                      const result = await uploadRevenue(file);
                      setUploadResult({ success: true, ...result });
                    } catch (err) {
                      setUploadResult({ success: false, error: err.response?.data?.detail || err.message });
                    } finally {
                      setUploading(false);
                      e.target.value = '';
                    }
                  }}
                />
              </label>
              {uploadResult && (
                <span style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  background: uploadResult.success ? '#dcfce7' : '#fee2e2',
                  color: uploadResult.success ? '#166534' : '#991b1b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  {uploadResult.success
                    ? `Saved to ${uploadResult.table_name} (${uploadResult.rows_inserted} rows, ${uploadResult.clients} clients)`
                    : `Error: ${uploadResult.error}`}
                  <span style={{ cursor: 'pointer' }} onClick={() => setUploadResult(null)}>&times;</span>
                </span>
              )}
            </>
          )}
          {/* Summary */}
          <span style={{
            background: '#f0fdf4',
            color: '#166534',
            padding: '8px 14px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            {boardData?.total_deals || 0} deals
          </span>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Board View - Filters + Metrics + Kanban */}
      {viewMode === 'board' && (() => {
        const metrics = boardData?.metrics || {};
        const owners = boardData?.owners || [];
        const fmtM = (v) => v >= 1000000 ? `USD ${(v/1000000).toFixed(2)}M` : v >= 1000 ? `USD ${(v/1000).toFixed(2)}K` : `USD ${v}`;
        const fmtAvg = (v) => v >= 1000 ? `USD ${(v/1000).toFixed(2)}K` : `USD ${v}`;

        // Apply client-side filters to columns
        const applyFilters = (deal) => {
          if (filterOwner && deal.deal_owner !== filterOwner) return false;
          return true;
        };

        const filteredColumns = (boardData?.columns || []).map(col => {
          if (!filterOwner) return col;
          const filtered = col.deals.filter(applyFilters);
          return {
            ...col,
            deals: filtered,
            deal_count: filtered.length,
            total_amount: Math.round(filtered.reduce((s, d) => s + d.amount, 0) * 100) / 100,
            total_weighted: Math.round(filtered.reduce((s, d) => s + d.weighted_amount, 0) * 100) / 100
          };
        });

        return (
        <>
        {/* Filter Bar — Deal Owner only */}
        <div style={{
          display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap',
          padding: '12px 0', marginBottom: '12px', borderBottom: '1px solid #f1f5f9'
        }}>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setOwnerDropdownOpen(prev => !prev)}
              style={{
                padding: '8px 14px', borderRadius: '6px',
                border: filterOwner ? '2px solid #2563eb' : '1px solid #e2e8f0',
                background: filterOwner ? '#dbeafe' : '#fff',
                cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                color: '#334155', display: 'flex', alignItems: 'center', gap: '6px',
                minWidth: '160px', justifyContent: 'space-between'
              }}
            >
              <span>{filterOwner || 'Deal owner'}</span>
              <span style={{ fontSize: '10px', color: '#64748b' }}>{ownerDropdownOpen ? '\u25B2' : '\u25BC'}</span>
            </button>
            {ownerDropdownOpen && (
              <>
                <div onClick={() => setOwnerDropdownOpen(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} />
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: '4px',
                  background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 999,
                  minWidth: '220px', padding: '6px 0', maxHeight: '300px', overflowY: 'auto'
                }}>
                  <div style={{ padding: '6px 12px' }}>
                    <input
                      type="text" placeholder="Search..." autoFocus
                      value={ownerSearch} onChange={e => setOwnerSearch(e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div
                    onClick={() => { setFilterOwner(''); setOwnerDropdownOpen(false); setOwnerSearch(''); }}
                    style={{
                      padding: '8px 14px', cursor: 'pointer', fontSize: '13px',
                      color: !filterOwner ? '#1e40af' : '#334155',
                      background: !filterOwner ? '#eff6ff' : 'transparent',
                      borderBottom: '1px solid #f1f5f9', fontWeight: !filterOwner ? '600' : '400'
                    }}
                  >All owners</div>
                  {owners.filter(o => o.toLowerCase().includes(ownerSearch.toLowerCase())).map(o => (
                    <div key={o}
                      onClick={() => { setFilterOwner(o); setOwnerDropdownOpen(false); setOwnerSearch(''); }}
                      style={{
                        padding: '8px 14px', cursor: 'pointer', fontSize: '13px',
                        color: filterOwner === o ? '#1e40af' : '#334155',
                        background: filterOwner === o ? '#eff6ff' : 'transparent',
                        fontWeight: filterOwner === o ? '600' : '400'
                      }}
                      onMouseOver={e => { if (filterOwner !== o) e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseOut={e => { if (filterOwner !== o) e.currentTarget.style.background = 'transparent'; }}
                    >{o}</div>
                  ))}
                </div>
              </>
            )}
          </div>
          {filterOwner && (
            <button onClick={() => setFilterOwner('')}
              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fee2e2', color: '#991b1b', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
              Clear filter
            </button>
          )}
        </div>

        {/* Metrics Bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: mobileLayout ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)',
          gap: '12px', marginBottom: '16px'
        }}>
          {[
            { label: 'TOTAL DEAL AMOUNT', value: fmtM(metrics.total_amount || 0), sub: `Average per deal`, subVal: fmtAvg(metrics.avg_per_deal || 0), color: '#1e293b' },
            { label: 'WEIGHTED DEAL AMOUNT', value: fmtM(metrics.weighted_amount || 0), sub: 'Average per deal', subVal: fmtAvg(metrics.weighted_avg || 0), color: '#0891b2' },
            { label: 'OPEN DEAL AMOUNT', value: fmtM(metrics.open_amount || 0), sub: 'Average per deal', subVal: fmtAvg(metrics.open_avg || 0), color: '#2563eb' },
            { label: 'CLOSED DEAL AMOUNT', value: fmtM(metrics.closed_amount || 0), sub: 'Average per deal', subVal: fmtAvg(metrics.closed_avg || 0), color: '#16a34a' },
            { label: 'NEW DEAL AMOUNT', value: fmtM(metrics.new_amount || 0), sub: 'Average per deal', subVal: fmtAvg(metrics.new_avg || 0), color: '#7c3aed' },
            { label: 'AVERAGE DEAL AGE', value: `${metrics.avg_deal_age_months || 0} months`, color: '#ea580c', noSub: true }
          ].map((m, idx) => (
            <div key={idx} style={{
              background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0',
              padding: '16px', textAlign: 'center'
            }}>
              <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600', marginBottom: '8px', letterSpacing: '0.5px' }}>{m.label}</div>
              <div style={{ fontSize: mobileLayout ? '18px' : '22px', fontWeight: '800', color: m.color }}>{m.value}</div>
              {!m.noSub && (
                <>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>{m.sub}</div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>{m.subVal}</div>
                </>
              )}
            </div>
          ))}
        </div>

      {/* Kanban Board */}
        <div style={{
          overflowX: 'auto',
          paddingBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            gap: '14px',
            minWidth: 'min-content',
            height: 'calc(100vh - 340px)'
          }}>
            {filteredColumns.map((col, idx) => (
              <StageColumn key={col.stage_id} column={col} colorIndex={idx} />
            ))}
            {filteredColumns.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b', fontSize: '15px', width: '100%' }}>
                No deals found for {selectedYears.join(', ')}.
              </div>
            )}
          </div>
        </div>
        </>
        );
      })()}

      {/* Report View - Quarterly Comparison */}
      {viewMode === 'report' && quarterlyData && (() => {
        const prev = quarterlyData.previous_quarter;
        const curr = quarterlyData.current_quarter;
        const fmtAmt = (v) => v >= 1000000 ? `USD ${(v/1000000).toFixed(1)}M` : v >= 1000 ? `USD ${(v/1000).toFixed(0)}K` : `USD ${v}`;
        const fmtAud = (v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : `${v}`;

        // Find revenue for a quarter label (e.g. "Q1 2026")
        const getRevenueForQuarter = (qLabel) => {
          if (!revenueData?.quarters) return null;
          return revenueData.quarters.find(q => q.label === qLabel);
        };
        const prevRevenue = getRevenueForQuarter(prev.label);
        const currRevenue = getRevenueForQuarter(curr.label);

        // Clickable metric card builder
        const MetricCard = ({ label, count, amount, color, borderColor, bgColor, quarter, type }) => {
          const isSelected = selectedDealView?.quarter === quarter.label && selectedDealView?.type === type;
          return (
            <div
              onClick={() => setSelectedDealView(isSelected ? null : { quarter: quarter.label, type, deals: quarter[`${type}_deals`], label: `${label} — ${quarter.label}` })}
              style={{
                padding: '14px 18px',
                borderRadius: '10px',
                border: isSelected ? `2px solid ${borderColor}` : '1px solid #e2e8f0',
                background: isSelected ? bgColor : '#fff',
                cursor: 'pointer',
                transition: 'all 0.15s',
                boxShadow: isSelected ? `0 0 0 3px ${borderColor}33` : 'none',
                minHeight: '90px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color }}>{count} <span style={{ fontSize: '13px', fontWeight: '500' }}>deals</span></div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginTop: '2px' }}>{fmtAmt(amount)}</div>
            </div>
          );
        };

        return (
        <>
        {/* Quarter Cards Side by Side */}
        <div style={{ display: 'grid', gridTemplateColumns: mobileLayout ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          {/* Previous Quarter */}
          <div style={{
            background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '20px', borderTop: '4px solid #94a3b8'
          }}>
            <h3 style={{ margin: '0 0 14px 0', fontSize: '17px', color: '#475569' }}>
              {prev.label} <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '400' }}>Last Quarter</span>
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: mobileLayout ? '1fr' : '1fr 1fr 1fr', gap: '10px' }}>
              <MetricCard label="Active" count={prev.active_count} amount={prev.active_amount} color="#2563eb" borderColor="#2563eb" bgColor="#eff6ff" quarter={prev} type="active" />
              <MetricCard label="Closed Won" count={prev.closed_count} amount={prev.closed_amount} color="#16a34a" borderColor="#16a34a" bgColor="#f0fdf4" quarter={prev} type="closed" />
              <MetricCard label="Lost / Abandoned" count={prev.lost_abandoned_count} amount={prev.lost_abandoned_amount} color="#ef4444" borderColor="#ef4444" bgColor="#fef2f2" quarter={prev} type="lost_abandoned" />
            </div>
            {/* Revenue & MRR - Clickable */}
            <div style={{ display: 'grid', gridTemplateColumns: mobileLayout ? '1fr' : '1fr 1fr', gap: '10px', marginTop: '12px' }}>
              {[
                { label: 'Contracted Revenue', data: prevRevenue, valueKey: 'total_k_aud', color: '#d97706', bgActive: '#fffbeb', bgEmpty: '#f8fafc', borderColor: '#d97706', type: 'revenue', qLabel: prev.label },
                { label: 'MRR (Total)', data: prevRevenue, valueKey: 'total_mrr_k_aud', color: '#0369a1', bgActive: '#f0f9ff', bgEmpty: '#f8fafc', borderColor: '#0369a1', type: 'mrr', qLabel: prev.label }
              ].map(card => {
                const hasData = card.data && card.data[card.valueKey];
                const isSelected = selectedDealView?.quarter === card.qLabel && selectedDealView?.type === card.type;
                return (
                  <div key={card.type} onClick={() => {
                    if (!card.data?.client_details) return;
                    setSelectedDealView(isSelected ? null : {
                      quarter: card.qLabel, type: card.type,
                      label: `${card.label} — ${card.qLabel}`,
                      revenueClients: card.data.client_details,
                      months: card.data.months
                    });
                  }} style={{
                    padding: '14px 18px', borderRadius: '10px',
                    border: isSelected ? `2px solid ${card.borderColor}` : '1px solid #e2e8f0',
                    background: isSelected ? card.bgActive : hasData ? card.bgActive : card.bgEmpty,
                    boxShadow: isSelected ? `0 0 0 3px ${card.borderColor}33` : 'none',
                    minHeight: '90px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    cursor: hasData ? 'pointer' : 'default', transition: 'all 0.15s'
                  }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>{card.label}</div>
                    {hasData ? (
                      <>
                        <div style={{ fontSize: '22px', fontWeight: '800', color: card.color }}>{fmtAud(card.data[card.valueKey] * 1000)}</div>
                        <div style={{ fontSize: '11px', fontWeight: '500', color: '#94a3b8' }}>K AUD{card.data.months ? ` (${card.data.months.length} months)` : ''}</div>
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#94a3b8' }}>No data</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Current Quarter */}
          <div style={{
            background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '20px', borderTop: '4px solid #2563eb'
          }}>
            <h3 style={{ margin: '0 0 14px 0', fontSize: '17px', color: '#1e293b' }}>
              {curr.label} <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: '400' }}>This Quarter</span>
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: mobileLayout ? '1fr' : '1fr 1fr 1fr', gap: '10px' }}>
              <MetricCard label="Active" count={curr.active_count} amount={curr.active_amount} color="#2563eb" borderColor="#2563eb" bgColor="#eff6ff" quarter={curr} type="active" />
              <MetricCard label="Closed Won" count={curr.closed_count} amount={curr.closed_amount} color="#16a34a" borderColor="#16a34a" bgColor="#f0fdf4" quarter={curr} type="closed" />
              <MetricCard label="Lost / Abandoned" count={curr.lost_abandoned_count} amount={curr.lost_abandoned_amount} color="#ef4444" borderColor="#ef4444" bgColor="#fef2f2" quarter={curr} type="lost_abandoned" />
            </div>
            {/* Revenue & MRR - Clickable */}
            <div style={{ display: 'grid', gridTemplateColumns: mobileLayout ? '1fr' : '1fr 1fr', gap: '10px', marginTop: '12px' }}>
              {[
                { label: 'Contracted Revenue', data: currRevenue, valueKey: 'total_k_aud', color: '#d97706', bgActive: '#fffbeb', bgEmpty: '#f8fafc', borderColor: '#d97706', type: 'revenue', qLabel: curr.label },
                { label: 'MRR (Total)', data: currRevenue, valueKey: 'total_mrr_k_aud', color: '#0369a1', bgActive: '#f0f9ff', bgEmpty: '#f8fafc', borderColor: '#0369a1', type: 'mrr', qLabel: curr.label }
              ].map(card => {
                const hasData = card.data && card.data[card.valueKey];
                const isSelected = selectedDealView?.quarter === card.qLabel && selectedDealView?.type === card.type;
                return (
                  <div key={card.type} onClick={() => {
                    if (!card.data?.client_details) return;
                    setSelectedDealView(isSelected ? null : {
                      quarter: card.qLabel, type: card.type,
                      label: `${card.label} — ${card.qLabel}`,
                      revenueClients: card.data.client_details,
                      months: card.data.months
                    });
                  }} style={{
                    padding: '14px 18px', borderRadius: '10px',
                    border: isSelected ? `2px solid ${card.borderColor}` : '1px solid #e2e8f0',
                    background: isSelected ? card.bgActive : hasData ? card.bgActive : card.bgEmpty,
                    boxShadow: isSelected ? `0 0 0 3px ${card.borderColor}33` : 'none',
                    minHeight: '90px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    cursor: hasData ? 'pointer' : 'default', transition: 'all 0.15s'
                  }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>{card.label}</div>
                    {hasData ? (
                      <>
                        <div style={{ fontSize: '22px', fontWeight: '800', color: card.color }}>{fmtAud(card.data[card.valueKey] * 1000)}</div>
                        <div style={{ fontSize: '11px', fontWeight: '500', color: '#94a3b8' }}>K AUD{card.data.months ? ` (${card.data.months.length} months)` : ''}</div>
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#94a3b8' }}>No data</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Dynamic Table — HubSpot Deals OR Revenue Clients */}
        {selectedDealView && (selectedDealView.deals || selectedDealView.revenueClients) && (
          <div style={{
            background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '24px', marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h4 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>
                {selectedDealView.label}
              </h4>
              <button
                onClick={() => setSelectedDealView(null)}
                style={{
                  padding: '4px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
                  background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#64748b'
                }}
              >
                Close
              </button>
            </div>

            {/* HubSpot Deals Table */}
            {selectedDealView.deals && (
              selectedDealView.deals.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '14px' }}>No deals in this category.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                        {[{k:'deal_name',l:'Deal Name'},{k:'amount',l:'Amount',a:'right'},{k:'expected_acv',l:'Expected ACV',a:'right'},{k:'stage',l:'Stage'},{k:'deal_owner',l:'Owner'},{k:'close_date',l:'Close Date'}].map(c=>(
                          <th key={c.k} onClick={()=>reqQtrSort(c.k)} style={{textAlign:c.a||'left',padding:'10px',color:'#1e40af',fontSize:'11px',fontWeight:'600',cursor:'pointer',userSelect:'none'}}>{c.l}{getQtrSortInd(c.k)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedQtrDeals.map((deal, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '10px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.deal_name}>{deal.deal_name}</td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>USD {deal.amount.toLocaleString()}</td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>USD {deal.expected_acv.toLocaleString()}</td>
                          <td style={{ padding: '10px' }}>{deal.stage}</td>
                          <td style={{ padding: '10px' }}>{deal.deal_owner}</td>
                          <td style={{ padding: '10px' }}>{deal.close_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* Revenue Client Table */}
            {selectedDealView.revenueClients && (
              selectedDealView.revenueClients.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '14px' }}>No revenue data.</div>
              ) : (() => {
                const clients = selectedDealView.revenueClients.filter(c => c.total_k_aud > 0);
                const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const monthKeys = selectedDealView.months ? selectedDealView.months.map(m => m.month.substring(0, 7)) : [];
                const formatMonthHeader = (m) => { const [y, mo] = m.split('-'); return `${y}-${MONTH_NAMES[parseInt(mo) - 1]}`; };
                return (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                          <th onClick={()=>reqRevSort('client')} style={{textAlign:'left',padding:'10px',color:'#1e40af',fontSize:'11px',fontWeight:'600',cursor:'pointer',userSelect:'none'}}>Client{getRevSortInd('client')}</th>
                          <th onClick={()=>reqRevSort('region')} style={{textAlign:'left',padding:'10px',color:'#1e40af',fontSize:'11px',fontWeight:'600',cursor:'pointer',userSelect:'none'}}>Region{getRevSortInd('region')}</th>
                          {monthKeys.map(m => (
                            <th key={m} style={{ textAlign: 'right', padding: '10px', color: '#1e40af', fontSize: '11px', fontWeight: '600' }}>{formatMonthHeader(m)}</th>
                          ))}
                          <th onClick={()=>reqRevSort('total_k_aud')} style={{textAlign:'right',padding:'10px',color:'#1e40af',fontSize:'11px',fontWeight:'600',cursor:'pointer',userSelect:'none'}}>Total (K AUD){getRevSortInd('total_k_aud')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(sortedRevClients.length > 0 ? sortedRevClients.filter(c => c.total_k_aud > 0) : clients).map((c, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '10px', fontWeight: '500' }}>{c.client}</td>
                            <td style={{ padding: '10px' }}>{c.region}</td>
                            {monthKeys.map(m => (
                              <td key={m} style={{ padding: '10px', textAlign: 'right' }}>{c.months?.[m] ? c.months[m].toFixed(1) : '-'}</td>
                            ))}
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '700' }}>{c.total_k_aud.toFixed(1)}</td>
                          </tr>
                        ))}
                        {/* Total row */}
                        <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc', fontWeight: '700' }}>
                          <td style={{ padding: '10px' }}>Total</td>
                          <td style={{ padding: '10px' }}></td>
                          {monthKeys.map(m => {
                            const sum = clients.reduce((s, c) => s + (c.months?.[m] || 0), 0);
                            return <td key={m} style={{ padding: '10px', textAlign: 'right' }}>{sum.toFixed(1)}</td>;
                          })}
                          <td style={{ padding: '10px', textAlign: 'right' }}>{clients.reduce((s, c) => s + c.total_k_aud, 0).toFixed(1)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </div>
        )}
        </>
        );
      })()}

      {/* Report View - Funnel Age */}
      {viewMode === 'report' && funnelAgeData && (() => {
        const buckets = funnelAgeData.buckets || [];
        const BUCKET_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#f97316', '#ef4444'];

        return (
          <div style={{
            background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '24px', marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#1e293b' }}>
                  How Long Have Deals Been in Funnel?
                </h4>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {funnelAgeData.total_deals} active deals | Avg: {funnelAgeData.avg_days} days | Median: {funnelAgeData.median_days} days | Max: {funnelAgeData.max_days} days
                </div>
              </div>
              {selectedBucket && (
                <span style={{
                  fontSize: '11px', background: '#fef3c7', color: '#92400e',
                  padding: '4px 12px', borderRadius: '12px', fontWeight: '600',
                  cursor: 'pointer'
                }} onClick={() => setSelectedBucket(null)}>
                  Showing: {selectedBucket.bucket} &times;
                </span>
              )}
            </div>

            <div style={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={buckets} margin={{ top: 20, right: 30, bottom: 5, left: 0 }}>
                  <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: '#334155' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} label={{ value: 'Number of Deals', angle: -90, position: 'insideLeft', offset: 10, fontSize: 12, fill: '#475569' }} />
                  <Tooltip
                    contentStyle={{ fontSize: '12px', borderRadius: '6px' }}
                    formatter={(value, name, props) => [
                      `${value} deals ($${props.payload.total_amount.toLocaleString()})`,
                      'Count'
                    ]}
                  />
                  <Bar
                    dataKey="count"
                    radius={[6, 6, 0, 0]}
                    barSize={60}
                    cursor="pointer"
                    onClick={(data) => {
                      setSelectedBucket(prev => prev?.bucket === data.bucket ? null : data);
                    }}
                  >
                    {buckets.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={selectedBucket?.bucket === entry.bucket ? BUCKET_COLORS[idx] : `${BUCKET_COLORS[idx]}99`}
                        stroke={selectedBucket?.bucket === entry.bucket ? BUCKET_COLORS[idx] : 'none'}
                        strokeWidth={selectedBucket?.bucket === entry.bucket ? 2 : 0}
                      />
                    ))}
                    <LabelList dataKey="count" position="top" fontSize={13} fontWeight={700} fill="#334155" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Deals table for selected bucket */}
            {selectedBucket && selectedBucket.deals && selectedBucket.deals.length > 0 && (
              <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h5 style={{ margin: 0, fontSize: '13px', color: '#1e293b' }}>
                    {selectedBucket.bucket} — {selectedBucket.deals.length} deals (${selectedBucket.total_amount.toLocaleString()})
                  </h5>
                </div>
                <div style={{ overflowX: 'auto', maxHeight: '350px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc', position: 'sticky', top: 0 }}>
                        {[{k:'deal_name',l:'Deal Name'},{k:'amount',l:'Amount',a:'right'},{k:'days_in_funnel',l:'Days in Funnel',a:'right'},{k:'stage',l:'Stage'},{k:'deal_owner',l:'Owner'},{k:'create_date',l:'Created'},{k:'close_date',l:'Close Date'}].map(c=>(
                          <th key={c.k} onClick={()=>reqFunnelSort(c.k)} style={{textAlign:c.a||'left',padding:'10px',color:'#1e40af',fontSize:'11px',fontWeight:'600',cursor:'pointer',userSelect:'none'}}>{c.l}{getFunnelSortInd(c.k)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFunnelDeals.map((deal, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '10px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.deal_name}>{deal.deal_name}</td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>USD {deal.amount.toLocaleString()}</td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: deal.days_in_funnel > 365 ? '#ef4444' : deal.days_in_funnel > 180 ? '#f97316' : '#334155' }}>
                            {deal.days_in_funnel}
                          </td>
                          <td style={{ padding: '10px' }}>{deal.stage}</td>
                          <td style={{ padding: '10px' }}>{deal.deal_owner}</td>
                          <td style={{ padding: '10px' }}>{deal.create_date}</td>
                          <td style={{ padding: '10px' }}>{deal.close_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Report View - Charts Side by Side */}
      {viewMode === 'report' && (
      <div>
        {/* Period Pill Tabs */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '16px',
          background: '#f1f5f9',
          borderRadius: '8px',
          padding: '4px',
          width: mobileLayout ? '100%' : 'fit-content',
          flexWrap: 'wrap'
        }}>
          {[
            { key: 'last_q', label: mobileLayout ? `Q${lastQ}` : `Last Quarter (Q${lastQ})` },
            { key: 'this_q', label: mobileLayout ? `Q${currentQ}` : `This Quarter (Q${currentQ})` },
            { key: 'year', label: 'Full Year' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setChartPeriod(tab.key)}
              style={{
                padding: mobileLayout ? '8px 12px' : '8px 18px',
                borderRadius: '6px',
                border: 'none',
                background: chartPeriod === tab.key ? '#1e40af' : 'transparent',
                color: chartPeriod === tab.key ? '#fff' : '#475569',
                fontSize: mobileLayout ? '12px' : '13px',
                fontWeight: chartPeriod === tab.key ? '700' : '500',
                cursor: 'pointer',
                transition: 'all 0.15s',
                flex: mobileLayout ? 1 : 'none',
                textAlign: 'center'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: mobileLayout ? '1fr' : '1fr 1fr',
        gap: '20px'
      }}>

      {/* Chart 1: Deal Count by Stage */}
      {stageCountData && stageCountData.length > 0 && (
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#1e293b' }}>
                All Deals by Deal Stage
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f97316', display: 'inline-block' }}></span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  {chartPeriodLabel} — includes all stages (active, won, lost, parked)
                </span>
              </div>
            </div>
            {selectedOwner && (
              <span style={{
                fontSize: '11px', background: '#dbeafe', color: '#1e40af',
                padding: '3px 10px', borderRadius: '12px', fontWeight: '600',
                cursor: 'pointer'
              }} onClick={() => { setSelectedOwner(null); setChartDrillDeals(null); }}>
                Filtered: {selectedOwner} &times;
              </span>
            )}
          </div>
          {(() => {
            // If an owner is selected in chart 2, filter stage counts
            const chartData = selectedOwner && stageOwnerData
              ? stageOwnerData.map(row => ({ stage: row.stage, count: row[selectedOwner] || 0 })).filter(r => r.count > 0)
              : stageCountData;
            return (
            <div style={{ height: 450 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, bottom: 30, left: 40 }}
                >
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    label={{ value: '(Count) Deals', position: 'bottom', offset: 10, fontSize: 12, fill: '#475569' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="stage"
                    tick={{ fontSize: 12, fill: '#334155' }}
                    width={120}
                    label={{ value: 'Deal Stage', angle: -90, position: 'insideLeft', offset: -25, fontSize: 12, fill: '#475569' }}
                  />
                  <Tooltip
                    formatter={(value) => [value, 'Deals']}
                    contentStyle={{ fontSize: '12px', borderRadius: '6px' }}
                  />
                  <Bar
                    dataKey="count"
                    radius={[0, 4, 4, 0]}
                    barSize={22}
                    cursor="pointer"
                    onClick={async (data) => {
                      const newStage = selectedStage === data.stage ? null : data.stage;
                      setSelectedStage(newStage);
                      setSelectedOwner(null);
                      if (newStage) {
                        try {
                          const quarter = getChartQuarter(chartPeriod);
                          const res = await getHubSpotDealsFiltered(reportYear, quarter, newStage, null);
                          setChartDrillDeals({ label: `${newStage} — ${chartPeriodLabel}`, deals: res.deals });
                        } catch (e) { console.error(e); }
                      } else {
                        setChartDrillDeals(null);
                      }
                    }}
                  >
                    {chartData.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={selectedStage === entry.stage ? '#ea580c' : '#f9a07a'}
                        stroke={selectedStage === entry.stage ? '#c2410c' : 'none'}
                        strokeWidth={selectedStage === entry.stage ? 2 : 0}
                      />
                    ))}
                    <LabelList dataKey="count" position="right" fontSize={12} fontWeight={600} fill="#334155" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            );
          })()}
        </div>
      )}

      {/* Chart 2: Deals by Deal Owner */}
      {stageOwnerData && stageOwnerData.length > 0 && (() => {
        // If a stage is selected in chart 1, show only that stage's owner breakdown
        const ownerTotals = {};
        if (selectedStage) {
          const stageRow = stageOwnerData.find(r => r.stage === selectedStage);
          if (stageRow) {
            for (const owner of stageOwnerOwners) {
              if (stageRow[owner] > 0) ownerTotals[owner] = stageRow[owner];
            }
          }
        } else {
          for (const row of stageOwnerData) {
            for (const owner of stageOwnerOwners) {
              ownerTotals[owner] = (ownerTotals[owner] || 0) + (row[owner] || 0);
            }
          }
        }
        const ownerChartData = Object.entries(ownerTotals)
          .map(([owner, count]) => ({ owner, count }))
          .sort((a, b) => b.count - a.count);

        return (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#1e293b' }}>
                  All Deals by Deal Owner
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }}></span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    {chartPeriodLabel} — total deals per owner across all stages
                  </span>
                </div>
              </div>
              {selectedStage && (
                <span style={{
                  fontSize: '11px', background: '#fff7ed', color: '#c2410c',
                  padding: '3px 10px', borderRadius: '12px', fontWeight: '600',
                  cursor: 'pointer'
                }} onClick={() => { setSelectedStage(null); setChartDrillDeals(null); }}>
                  Filtered: {selectedStage} &times;
                </span>
              )}
            </div>
            <div style={{ height: 450 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={ownerChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, bottom: 30, left: 40 }}
                >
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    label={{ value: '(Count) Deals', position: 'bottom', offset: 10, fontSize: 12, fill: '#475569' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="owner"
                    tick={{ fontSize: 12, fill: '#334155' }}
                    width={120}
                    label={{ value: 'Deal Owner', angle: -90, position: 'insideLeft', offset: -25, fontSize: 12, fill: '#475569' }}
                  />
                  <Tooltip
                    formatter={(value) => [value, 'Deals']}
                    contentStyle={{ fontSize: '12px', borderRadius: '6px' }}
                  />
                  <Bar
                    dataKey="count"
                    radius={[0, 4, 4, 0]}
                    barSize={22}
                    cursor="pointer"
                    onClick={async (data) => {
                      const newOwner = selectedOwner === data.owner ? null : data.owner;
                      setSelectedOwner(newOwner);
                      setSelectedStage(null);
                      if (newOwner) {
                        try {
                          const quarter = getChartQuarter(chartPeriod);
                          const res = await getHubSpotDealsFiltered(reportYear, quarter, null, newOwner);
                          setChartDrillDeals({ label: `${newOwner} — ${chartPeriodLabel}`, deals: res.deals });
                        } catch (e) { console.error(e); }
                      } else {
                        setChartDrillDeals(null);
                      }
                    }}
                  >
                    {ownerChartData.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={selectedOwner === entry.owner ? '#2563eb' : '#93c5fd'}
                        stroke={selectedOwner === entry.owner ? '#1e40af' : 'none'}
                        strokeWidth={selectedOwner === entry.owner ? 2 : 0}
                      />
                    ))}
                    <LabelList dataKey="count" position="right" fontSize={12} fontWeight={600} fill="#334155" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })()}

      </div>

      {/* Chart Drill-Down Deals Table */}
      {chartDrillDeals && chartDrillDeals.deals && (
        <div style={{
          background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          padding: '24px', marginTop: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <h4 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>
              {chartDrillDeals.label}
              <span style={{ fontSize: '12px', fontWeight: '400', color: '#64748b', marginLeft: '8px' }}>
                ({chartDrillDeals.deals.length} deals — USD {chartDrillDeals.deals.reduce((s, d) => s + d.amount, 0).toLocaleString()})
              </span>
            </h4>
            <button
              onClick={() => { setChartDrillDeals(null); setSelectedStage(null); setSelectedOwner(null); }}
              style={{
                padding: '4px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
                background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#64748b'
              }}
            >
              Close
            </button>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc', position: 'sticky', top: 0 }}>
                  {[{k:'deal_name',l:'Deal Name'},{k:'amount',l:'Amount',a:'right'},{k:'expected_acv',l:'Expected ACV',a:'right'},{k:'stage',l:'Stage'},{k:'deal_owner',l:'Owner'},{k:'close_date',l:'Close Date'},{k:'is_won',l:'Status'}].map(c=>(
                    <th key={c.k} onClick={()=>reqDrillSort(c.k)} style={{textAlign:c.a||'left',padding:'10px',color:'#1e40af',fontSize:'11px',fontWeight:'600',cursor:'pointer',userSelect:'none'}}>{c.l}{getDrillSortInd(c.k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedDrillDeals.map((deal, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '10px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.deal_name}>{deal.deal_name}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>USD {deal.amount.toLocaleString()}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>USD {deal.expected_acv.toLocaleString()}</td>
                    <td style={{ padding: '10px' }}>{deal.stage}</td>
                    <td style={{ padding: '10px' }}>{deal.deal_owner}</td>
                    <td style={{ padding: '10px' }}>{deal.close_date}</td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px',
                        background: deal.is_won ? '#dcfce7' : deal.is_lost ? '#fee2e2' : '#dbeafe',
                        color: deal.is_won ? '#166534' : deal.is_lost ? '#991b1b' : '#1e40af'
                      }}>
                        {deal.is_won ? 'Won' : deal.is_lost ? 'Lost' : 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </div>
      )}
    </div>
  );
}

export default DealsBoardDashboard;
